import { FormControl, FormGroup, Validators } from "@angular/forms";
import { BaseModalComponent } from "src/app/core/base-modal.componant";
import { BookingStatusEnum } from "src/app/shared/models/booking-status.enum";
import { Simulator } from "../../models/simulators";
import { Subscription } from "rxjs";
import { SessionService } from "src/app/shared/services/session.service";
import { BookingService } from "../../services/booking.service";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { addMinutes } from "date-fns";
import { AddonService } from "../../services/addon.service";
import { Addon } from "../../models/addon";
import { AlertModalComponent } from "src/app/shared/modals/alert-modal/alert-modal.component";
import { EventEmitter, inject } from "@angular/core";

export class BaseBookingModalComponent<TModal> extends BaseModalComponent {

    public form = new FormGroup({
        simulatorId: new FormControl('', {
            validators: Validators.required
        }),
        startTime: new FormControl('', {
            validators: Validators.required
        }),
        endTime: new FormControl('', {
            validators: Validators.required
        }),
        status: new FormControl(BookingStatusEnum.Booked, {
            validators: Validators.required
        }),
        phoneNumber: new FormControl(''),
        email: new FormControl(''),
        customerName: new FormControl('', {
            validators: Validators.required
        }),
        totalPrice: new FormControl(0.00, {
            validators: Validators.required
        }),
        priceOverride: new FormControl(null),
        priceOverrideReason: new FormControl(null),
        hasPaid: new FormControl(false),
        participants: new FormControl(null, {
            validators: Validators.required
        }),
        organizationId: new FormControl('', {
            validators: Validators.required
        }),
        duration: new FormControl(null, {
            validators: Validators.required
        }),
        addons: new FormControl([]),
        createdBy: new FormControl(),
    });

    public simulator: Simulator = new Simulator();
    public startTime: Date;
    public availableDurations: any[] = [];
    public availableAddons: Addon[] = [];
    public selectedAddons: Addon[] = [];

    priceOverrideSubscription: Subscription;
    bookingStatusEnum = BookingStatusEnum;
    bookingStatusEnumKeys = Object.keys(BookingStatusEnum).filter(key => isNaN(Number(key)));

    constructor(protected sessionService: SessionService,
        protected bookingService: BookingService,
        protected addonService: AddonService,
        public dialogRef: MatDialogRef<TModal>,
        protected dialog: MatDialog
    ) {
        super();
        this.form.get('organizationId').setValue(this.sessionService.getUser().organizationId);
        this.form.get('createdBy').setValue(this.sessionService.getUserId());
    }

    getAddons() {
        this.isLoading = true;
        this.addonService.getAllByOrganization({ organizationId: this.sessionService.getUser().organizationId }).subscribe({
            next: (result) => {
                this.availableAddons = result.results;
            },
            error: (error) => {
                console.error(error);
            },
            complete: () => this.isLoading = false
        });
    }

    getBookingAvailability() {
        const startLocalDate = new Date(this.startTime);

        const startUtcDate = new Date(Date.UTC(
            startLocalDate.getFullYear(),
            startLocalDate.getMonth(),
            startLocalDate.getDate(),
            startLocalDate.getHours(),
            startLocalDate.getMinutes()
        ));

        let data = {
            simulatorId: this.form.get('simulatorId').value,
            bookingDate: startUtcDate.toISOString(),
            organizationId: this.sessionService.getUser().organizationId
        };

        this.isLoading = true;
        this.bookingService.getAvailableDurations(data).subscribe({
            next: (result) => {
                this.availableDurations = result;
            },
            error: (error) => {
                this.isLoading = false;
                console.error(error);
                this.setError(error, this.form);
            },
            complete: () => this.isLoading = false
        });
    }

    setEndTimeAndTotalPrice(duration: any, timeslot: any = null) {
        this.form.get('totalPrice').setValue(duration.price);
        let endTime = addMinutes(this.form.get('startTime').value, duration.durationMinutes);
        this.form.get('endTime').setValue(endTime.toString());
    }

    alert(title, body, submitText, eventEmitter: EventEmitter<any> = null) {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: 'auto',
        });

        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.body = body;
        dialogRef.componentInstance.submitText = submitText;

        this.dialogRef.afterClosed().subscribe(() => {
            if (eventEmitter) {
                eventEmitter.emit();
            }
        });
    }

    normalizeDateTime(dateTime: any): string {
        try {
            // Parse the date/time value
            const date = new Date(dateTime);

            // Check if the date is valid
            if (isNaN(date.getTime())) {
                throw new Error('Invalid date');
            }

            // Return ISO string without timezone info (just the local time components)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        } catch (error) {
            console.error('Error normalizing date/time:', error);
            // Fallback to current time if parsing fails
            return new Date().toISOString().slice(0, 19);
        }
    }
}